import React, { useContext, useLayoutEffect, useState } from 'react';
import { Button, AppHeader, Typography, Stack, Box, AppHeaderBackButton } from '@ace-clublabs/shared-component-library';
import { MembershipAppStateContext } from '../components/MembershipAppState/MembershipAppState';
import { UtagType } from '../utils/UtagHelper';
import customer_service_image from '../assets/customer-service-image.svg';
import city_view_image from '../assets/city-view-image.svg';
import { FlowType, VoucherStatus } from '@ace-clublabs/common-membership-api-types';

interface IKnockoutPageProps {
  errorType: 'voucherError' | 'memberExists' | 'InvalidGrouponStates';
}

const partnerLabelMap = {
  [FlowType.GROUPON as string]: 'Groupon',
};

const voucherStatusTextMap = {
  [VoucherStatus.Redeemed]: 'been redeemed',
  [VoucherStatus.Refunded]: 'been refunded',
  [VoucherStatus.Expired]: 'expired',
};

const partnerWebsiteMap = {
  [FlowType.GROUPON as string]: 'https://www.groupon.com/',
};

export const KnockoutPage: React.FC<IKnockoutPageProps> = (props: IKnockoutPageProps) => {
  const errorType = props?.errorType || '';
  const { sendTag, sessionData } = useContext(MembershipAppStateContext);
  const [isTagging, setIsTagging] = useState(true);
  const flow = sessionData.flowType ?? FlowType.GROUPON; //currently only used for groupon
  const tokenStatus = sessionData?.tokenStatus;

  const errorTagReason = () => {
    if (errorType === 'memberExists') return 'membership:join:knockout-existing member found';
    else if (errorType === 'InvalidGrouponStates')
      return 'membership:join:error page-the offer is not available within state provided';
    else if (errorType === 'voucherError') return voucherStatusTagReason();
    else return 'membership:join:error page';
  };

  const voucherStatusTagReason = () => {
    switch (tokenStatus) {
      case VoucherStatus.Expired:
        return 'membership:join:error page-the voucher has expired';
      case VoucherStatus.Redeemed:
        return 'membership:join:error page-the voucher has already been redeemed';
      case VoucherStatus.Refunded:
        return 'membership:join:error page-the voucher has been refunded';
      default:
        return;
    }
  };

  useLayoutEffect(() => {
    sendTag(UtagType.view, 'error page', {
      error: errorTagReason(),
    });
    setIsTagging(false);
  }, []);

  if (isTagging) {
    return <></>;
  }

  return (
    <Box className="knockout-page-view">
      <AppHeader sx={{ padding: 0 }} backButton={errorType === 'InvalidGrouponStates' && <AppHeaderBackButton />} />
      <Box>
        <Stack className="knockout-container">
          <img
            src={errorType === 'InvalidGrouponStates' ? city_view_image : customer_service_image}
            className="knock-out-page-image"
            style={{ marginTop: '3rem' }}
          />
          <Typography variant="h5Medium" className="knockout-title" sx={{ marginTop: '1.5rem' }}>
            {getKnockoutTitle(errorType, tokenStatus, flow)}
          </Typography>
          <Typography variant="b1Regular" className="knockout-description" sx={{ margin: '1rem 0' }}>
            {getKnockoutMessage(errorType, flow)}
          </Typography>
        </Stack>
        <Button
          label={`Return to ${partnerLabelMap[flow]}`}
          fullWidth
          onClick={() => {
            window.location.assign(partnerWebsiteMap[flow]);
          }}
          variant="contained"
          size="medium"
          id="knockoutActionButton"
        ></Button>
      </Box>
    </Box>
  );
};

function getKnockoutTitle(errorType: string, voucherIssue?: any, partnerName?: FlowType) {
  const partner = partnerLabelMap[partnerName as FlowType] ? ` ${partnerLabelMap[partnerName as FlowType]}` : '';
  switch (errorType) {
    case 'voucherError':
      return `Your${partner} voucher has ${
        //@ts-ignore
        voucherIssue ? voucherStatusTextMap[voucherIssue as VoucherStatus] : 'an error'
      }`;
    case 'memberExists':
      return 'We found an existing membership';
    case 'InvalidGrouponStates':
      return 'The Groupon offer is not available within the state you provided';
    default:
      return "We couldn't load your information";
  }
}

function getKnockoutMessage(errorType: string, partnerName?: FlowType) {
  switch (errorType) {
    case 'voucherError':
      return `Please contact ${partnerLabelMap[partnerName as FlowType] || 'the vendor'} for assistance.`;
    case 'memberExists':
      return `If you're currently a AAA member, please return to ${
        partnerLabelMap[partnerName as FlowType] || 'the vendor'
      } to review the terms and conditions and request a refund.`;
    case 'InvalidGrouponStates':
      return 'Please go back to correct your address or return to Groupon to review the terms and conditions.';
    default:
      return 'Please try again later.';
  }
}
